import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import { supabase } from '../config/supabase';
import './CustomerProfile.css';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    delivery_address: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    try {
      const customerData = localStorage.getItem('customer');
      if (!customerData) {
        navigate('/login');
        return;
      }

      const customer = JSON.parse(customerData);
      setCustomer(customer);
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        delivery_address: customer.delivery_address || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading customer data:', error);
      navigate('/login');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    // Reset form to original customer data
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      delivery_address: customer.delivery_address || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      setSaving(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setSaving(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      setSaving(false);
      return;
    }

    try {
      if (!customer || !customer.id) {
        setError('Customer ID not found. Please login again.');
        setSaving(false);
        return;
      }

      // Update customer in Supabase
      const { data, error: updateError } = await supabase
        .from('customers')
        .update({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          delivery_address: formData.delivery_address.trim() || null
        })
        .eq('id', customer.id)
        .select()
        .single();

      if (updateError) {
        // Check if email already exists
        if (updateError.code === '23505') {
          setError('This email is already registered. Please use a different email.');
        } else {
          throw updateError;
        }
        setSaving(false);
        return;
      }

      // Update localStorage
      const updatedCustomer = {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        delivery_address: data.delivery_address
      };
      localStorage.setItem('customer', JSON.stringify(updatedCustomer));

      // Update state
      setCustomer(updatedCustomer);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError('');

    try {
      if (!customer || !customer.id) {
        setError('Customer ID not found. Please login again.');
        setDeleting(false);
        return;
      }

      // Deactivate account by setting is_active to false
      const { error: deleteError } = await supabase
        .from('customers')
        .update({ is_active: false })
        .eq('id', customer.id);

      if (deleteError) {
        throw deleteError;
      }

      // Clear localStorage
      localStorage.removeItem('customer');
      
      // Show success message briefly
      setSuccess('Account deleted successfully. You will be redirected...');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
        window.location.reload(); // Reload to update header state
      }, 2000);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="customer-profile-page">
        <CustomerHeader />
        <div className="customer-profile-loading">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-profile-page">
      <CustomerHeader />
      
      <div className="customer-profile-main">
        <div className="customer-profile-container">
          <div className="customer-profile-header">
            <h1 className="customer-profile-title">My Profile</h1>
            <p className="customer-profile-subtitle">Manage your account information</p>
          </div>

          {error && (
            <div className="customer-profile-message customer-profile-error">
              {error}
            </div>
          )}

          {success && (
            <div className="customer-profile-message customer-profile-success">
              {success}
            </div>
          )}

          <div className="customer-profile-card">
            <div className="customer-profile-card-header">
              <h2 className="customer-profile-card-title">Personal Information</h2>
              {!isEditing && (
                <button
                  className="customer-profile-edit-btn"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </div>

            <form onSubmit={handleSave} className="customer-profile-form">
              <div className="customer-profile-form-group">
                <label htmlFor="name" className="customer-profile-label">
                  Full Name <span className="customer-profile-required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  required
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="email" className="customer-profile-label">
                  Email Address <span className="customer-profile-required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  required
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="phone" className="customer-profile-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  placeholder="+92 300 1234567"
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="delivery_address" className="customer-profile-label">
                  Delivery Address
                </label>
                <textarea
                  id="delivery_address"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-textarea"
                  rows="4"
                  placeholder="Enter your delivery address"
                />
              </div>

              {isEditing && (
                <div className="customer-profile-form-actions">
                  <button
                    type="button"
                    className="customer-profile-cancel-btn"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="customer-profile-save-btn"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>

            {/* Delete Account Section */}
            {!isEditing && (
              <div className="customer-profile-delete-section">
                <div className="customer-profile-delete-divider"></div>
                <div className="customer-profile-delete-content">
                  <h3 className="customer-profile-delete-title">Delete Account</h3>
                  <p className="customer-profile-delete-description">
                    Once you delete your account, you will not be able to login again. This action cannot be undone.
                  </p>
                  <button
                    type="button"
                    className="customer-profile-delete-btn"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="customer-profile-modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="customer-profile-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="customer-profile-modal-title">Delete Account</h2>
            <p className="customer-profile-modal-message">
              Are you sure you want to delete your account? This action cannot be undone and you will not be able to login again.
            </p>
            <div className="customer-profile-modal-actions">
              <button
                type="button"
                className="customer-profile-modal-cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="customer-profile-modal-delete-btn"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;

